import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, User, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchUserIncidents } from '../services/incidents';
import { fetchUserProfile } from '../services/users';
import { userAtom } from '../atoms/auth';
import IncidentCard from './IncidentCard';
import Header from './Header';
import { Button } from './ui/button';

export default function ProfilePage() {
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);
  
  const { data: userIncidents, isLoading: isLoadingIncidents } = useQuery({
    queryKey: ['userIncidents'],
    queryFn: fetchUserIncidents,
    enabled: !!user,
    staleTime: 60000,
  });
  
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
    enabled: !!user,
    staleTime: 60000,
  });
  
  if (!user) {
    return (
      <div className="h-[100dvh] w-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">{t('profile.notLoggedIn')}</h2>
            <p className="text-gray-500 mb-4">{t('profile.loginPrompt')}</p>
            <Link to="/">
              <Button variant="default" className="bg-red-600 hover:bg-red-700">
                {t('common.backToHome')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  const isLoading = isLoadingIncidents || isLoadingProfile;
  
  return (
    <div className="h-[100dvh] w-screen flex flex-col">
      <Header />
      
      <main className="flex-1 pt-16 pb-8 overflow-auto">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center mb-6 mt-4">
            <Link to="/" className="mr-4">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <User className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {userProfile?.username || user.email}
                </h2>
                <p className="text-gray-500">{t('profile.member')}</p>
                {userProfile?.created_at && (
                  <p className="text-sm text-gray-500">
                    {t('profile.memberSince', { 
                      date: new Date(userProfile.created_at).toLocaleDateString() 
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <h2 className="text-xl font-bold mb-4">{t('profile.yourIncidents')}</h2>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : userIncidents && userIncidents.length > 0 ? (
            <div className="space-y-4">
              {userIncidents.map(incident => (
                <IncidentCard 
                  key={incident.id} 
                  incident={incident} 
                  isProfileView={true} 
                  onDelete={(deletedId) => {
                    // Optionally handle deletion in the parent component
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="mb-4">
                <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {t('profile.noIncidents')}
              </h3>
              <p className="text-gray-500 mb-4">
                {t('profile.noIncidentsDescription')}
              </p>
              <Link to="/">
                <Button variant="default" className="bg-red-600 hover:bg-red-700">
                  {t('profile.reportIncident')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
